import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Calendar, User, FileText, Award } from "lucide-react";
import { API } from "../App";

const COURSE_LABELS = {
  primeiros_socorros: "Primeiros Socorros",
  epi_nr06: "EPI / NR 06",
  integracao: "Integração",
  combate_incendio: "Combate a Incêndio",
  cipa: "CIPA – Designado",
  direcao_defensiva: "Direção Defensiva",
};

export default function PublicCertificateValidation() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/verificar/${id}`);
        if (mounted) setState({ loading: false, error: null, data });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Falha ao validar certificado";
        if (mounted) setState({ loading: false, error: msg, data: null });
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full" data-testid="cert-validation-card">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header strip */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 flex items-center gap-3 text-white">
            <ShieldCheck className="w-7 h-7" />
            <div>
              <p className="text-xs uppercase tracking-wider opacity-90">AVI Segurança</p>
              <h1 className="text-xl font-semibold">Validação de Certificado</h1>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {state.loading && (
              <div className="flex flex-col items-center py-12 gap-3" data-testid="cert-validation-loading">
                <Loader2 className="w-10 h-10 animate-spin text-slate-500" />
                <p className="text-slate-600">Verificando autenticidade…</p>
              </div>
            )}

            {!state.loading && state.error && (
              <div className="text-center py-10" data-testid="cert-validation-invalid">
                <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Certificado inválido</h2>
                <p className="text-slate-600">{state.error}</p>
                <p className="text-sm text-slate-400 mt-4">ID consultado: <span className="font-mono">{id}</span></p>
              </div>
            )}

            {!state.loading && state.data && (
              <div data-testid="cert-validation-valid">
                <div className="flex flex-col items-center text-center mb-6">
                  <CheckCircle2 className="w-16 h-16 text-emerald-500 mb-2" />
                  <h2 className="text-2xl font-bold text-slate-900">Certificado autêntico</h2>
                  <p className="text-slate-500 text-sm">Emitido por AVI Segurança</p>
                </div>

                <dl className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                  <Row icon={Award} label="Treinamento" value={
                    COURSE_LABELS[state.data.course_key] || state.data.course_titulo || state.data.course_key
                  } testId="cert-course" />
                  <Row icon={User} label="Funcionário" value={state.data.nome} testId="cert-name" />
                  <Row icon={FileText} label="CPF" value={state.data.cpf_masked} testId="cert-cpf" />
                  <Row icon={Calendar} label="Data do Treinamento" value={state.data.training_date} testId="cert-date" />
                  {state.data.course_carga_horaria && (
                    <Row icon={Calendar} label="Carga Horária" value={state.data.course_carga_horaria} testId="cert-load" />
                  )}
                  {state.data.instrutor && (
                    <Row icon={User} label="Instrutor" value={state.data.instrutor} testId="cert-instructor" />
                  )}
                  <Row icon={ShieldCheck} label="ID de Verificação" value={state.data.cert_id} testId="cert-id" mono />
                  <Row icon={Calendar} label="Emitido em" value={formatDate(state.data.emitted_at)} testId="cert-emitted" />
                </dl>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-slate-100 text-center text-xs text-slate-400">
              <Link to="/" className="hover:text-slate-600" data-testid="cert-back-link">
                Voltar ao painel AVI Segurança
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, testId, mono }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3" data-testid={testId}>
      <dt className="text-sm text-slate-500 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </dt>
      <dd className={`col-span-2 text-sm text-slate-900 font-medium ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </dd>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
}
